// The planner view model (REQ-VIEW-003..008) — pure functions over the RLS-scoped item list.
// This is the successor to bucketItems (REQ-VIEW-001, deprecated): instead of status buckets it
// produces a calm Today view (slipped + today + a next-few-days peek + anytime) and a peer Week
// agenda. All timezone-aware via the shared date helpers; no I/O, no DOM. Everything is a
// projection over the unified `item` — no new entity (ADR-003 / B-001).
import { addDays, localDateStr, weekdayMon1, type ViewItem } from "./view";
import type { Draft } from "./extract";
import { occurrencesBetween } from "./recurrence";

export type ViewName = "today" | "week";
const TZ = "Pacific/Auckland";

export interface DayGroup {
  date: string; // YYYY-MM-DD
  label: string; // "Today" | "Tomorrow" | "Mon" | …
  items: ViewItem[];
}

export interface TodayModel {
  today: string;
  slipped: ViewItem[]; // overdue, oldest first
  todayItems: ViewItem[];
  nextDays: DayGroup[]; // the next-few-days peek
  anytime: ViewItem[]; // open undated to-dos
}

export interface WeekModel {
  today: string;
  days: DayGroup[]; // today … today+6
}

const WD_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WD_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const isOpen = (i: ViewItem): boolean => i.status === "open";
const dueDate = (i: ViewItem, tz: string): string | null =>
  i.due_at ? localDateStr(new Date(i.due_at), tz) : null;

// ── Recurring-event expansion (REQ-CAL-011) ────────────────────────────────────────────────────
// A recurring EVENT is stored once (due_at = first occurrence, rrule set); the views project it
// onto every occurrence within the visible window. Pure: each occurrence is a clone with a synthetic
// id and the same captured duration (end_at shifted to match). Non-recurring items pass through, and
// a recurring TASK is NOT expanded here — its due_at is kept current by roll-forward on completion.
export function expandRecurringEvents(items: ViewItem[], fromISO: string, toISO: string, max = 120): ViewItem[] {
  const out: ViewItem[] = [];
  for (const it of items) {
    if (it.type === "event" && it.rrule && it.due_at) {
      const durationMs = it.end_at ? new Date(it.end_at).getTime() - new Date(it.due_at).getTime() : 0;
      const occ = occurrencesBetween(it.rrule, it.due_at, fromISO, toISO, max);
      if (occ.length) {
        for (const iso of occ) {
          out.push({
            ...it,
            id: `${it.id}#${iso.slice(0, 10)}`,
            due_at: iso,
            end_at: durationMs > 0 ? new Date(new Date(iso).getTime() + durationMs).toISOString() : it.end_at ?? null,
            recur_source_id: it.id, // the stored master row (for open/edit), REQ-CAL-011
          });
        }
        continue; // the master row itself is not shown — only its occurrences
      }
    }
    out.push(it);
  }
  return out;
}

// A generous ISO window around a local-date span [fromDate, toDate] (pad ±2 days to cover the NZ
// offset), so recurring occurrences are generated before the per-model local-date bucketing runs.
function windowISO(fromDate: string, toDate: string): [string, string] {
  return [`${addDays(fromDate, -2)}T00:00:00Z`, `${addDays(toDate, 2)}T00:00:00Z`];
}

/** True for a to-do lane item (task/grocery/note) vs an event. */
export function isTodo(i: ViewItem): boolean {
  return i.type !== "event";
}

/** Split a day's items into the two lanes the design shows (keyed on `type`, REQ-VIEW-004). */
export function partitionByType(items: ViewItem[]): { events: ViewItem[]; todos: ViewItem[] } {
  return {
    events: items.filter((i) => i.type === "event"),
    todos: items.filter(isTodo),
  };
}

/** Relative day label for the agenda/peek. */
export function dayLabel(dateStr: string, todayStr: string): string {
  if (dateStr === todayStr) return "Today";
  if (dateStr === addDays(todayStr, 1)) return "Tomorrow";
  return WD_SHORT[weekdayMon1(dateStr) - 1];
}

// events time-led, then to-dos; within, by due time, then title — a stable, glanceable order.
function ordered(items: ViewItem[]): ViewItem[] {
  return [...items].sort((a, b) => {
    const ta = a.due_at ?? "";
    const tb = b.due_at ?? "";
    if (ta !== tb) return ta < tb ? -1 : 1;
    return a.title.localeCompare(b.title);
  });
}

/** Overdue open items, oldest first — the calm "Slipped" section (REQ-VIEW-006). Empty ⇒ []. */
// Slipped = overdue TO-DOS only (never past events or groceries) — a past event isn't "slipped",
// and can't be cleared. Matches tasksModel.overdue so the Today count == the Slipped sheet's list.
export function attentionStrip(items: ViewItem[], nowISO: string, tz = TZ): ViewItem[] {
  const today = localDateStr(new Date(nowISO), tz);
  return ordered(
    items.filter((i) => isOpen(i) && isTodo(i) && i.type !== "grocery" && dueDate(i, tz) !== null && dueDate(i, tz)! < today)
  );
}

// ── Today-hero: the demoted Slipped line (REQ-VIEW-019) ────────────────────────────────────────
export interface SlippedLine {
  count: number;
  firstTitle: string; // the oldest overdue item's title
}
/** The single quiet Slipped line for Today — a count + the first title, opening Tasks→Overdue.
 *  Null when nothing is overdue (Today never shows a bare "all clear"). */
export function slippedLine(overdue: ViewItem[]): SlippedLine | null {
  if (!overdue.length) return null;
  return { count: overdue.length, firstTitle: overdue[0].title };
}

/** Open undated to-dos — the small "Anytime" section (REQ-VIEW-008). A filter, not a new entity. */
export function undatedAnytime(items: ViewItem[]): ViewItem[] {
  return [...items]
    .filter((i) => isOpen(i) && !i.due_at && isTodo(i))
    .sort((a, b) => a.title.localeCompare(b.title));
}

// ── Tasks surface (REQ-VIEW-015) — a projection over `item`, not a new entity ──────────────────
export interface TasksModel {
  overdue: ViewItem[];
  today: ViewItem[];
  later: ViewItem[];
  anytime: ViewItem[]; // absorbs the old standalone Anytime lane (REQ-VIEW-008, folded in)
}

/** The Tasks surface: open to-do items (task/note — events live in Calendar, groceries in Trolley)
 *  bucketed Overdue / Today / Later / Anytime. A pure projection over the unified `item`. */
export function tasksModel(items: ViewItem[], nowISO: string, tz = TZ): TasksModel {
  const today = localDateStr(new Date(nowISO), tz);
  const out: TasksModel = { overdue: [], today: [], later: [], anytime: [] };
  for (const it of items) {
    if (!isOpen(it) || !isTodo(it) || it.type === "grocery") continue;
    const due = dueDate(it, tz);
    if (due === null) out.anytime.push(it);
    else if (due < today) out.overdue.push(it);
    else if (due === today) out.today.push(it);
    else out.later.push(it);
  }
  out.overdue = ordered(out.overdue);
  out.today = ordered(out.today);
  out.later = ordered(out.later);
  out.anytime = [...out.anytime].sort((a, b) => a.title.localeCompare(b.title));
  return out;
}

/** The Today view model (REQ-VIEW-003): slipped + today + a next-few-days peek + anytime. */
export function todayModel(items: ViewItem[], nowISO: string, tz = TZ, peekDays = 3): TodayModel {
  const today = localDateStr(new Date(nowISO), tz);
  const [wf, wt] = windowISO(today, addDays(today, peekDays));
  const src = expandRecurringEvents(items, wf, wt);
  const byDate = new Map<string, ViewItem[]>();
  const todayItems: ViewItem[] = [];
  for (const it of src) {
    if (!isOpen(it)) continue;
    const due = dueDate(it, tz);
    if (due === null || due < today) continue; // undated → anytime; overdue → slipped
    if (due === today) todayItems.push(it);
    else (byDate.get(due) ?? byDate.set(due, []).get(due)!).push(it);
  }
  const nextDays: DayGroup[] = [];
  for (let n = 1; n <= peekDays; n++) {
    const d = addDays(today, n);
    nextDays.push({ date: d, label: dayLabel(d, today), items: ordered(byDate.get(d) ?? []) });
  }
  return {
    today,
    slipped: attentionStrip(items, nowISO, tz),
    todayItems: ordered(todayItems),
    nextDays,
    anytime: undatedAnytime(items),
  };
}

/** The Week view model (REQ-VIEW-003): a 7-day forward agenda, same day components. */
export function weekAgenda(items: ViewItem[], nowISO: string, tz = TZ, days = 7): WeekModel {
  const today = localDateStr(new Date(nowISO), tz);
  const [wf, wt] = windowISO(today, addDays(today, days));
  const byDate = new Map<string, ViewItem[]>();
  for (const it of expandRecurringEvents(items, wf, wt)) {
    if (!isOpen(it)) continue;
    const due = dueDate(it, tz);
    if (due === null || due < today) continue;
    (byDate.get(due) ?? byDate.set(due, []).get(due)!).push(it);
  }
  const out: DayGroup[] = [];
  for (let n = 0; n < days; n++) {
    const d = addDays(today, n);
    out.push({ date: d, label: dayLabel(d, today), items: ordered(byDate.get(d) ?? []) });
  }
  return { today, days: out };
}

/** The Calendar Week model (REQ-VIEW-016/018) — the Monday–Sunday calendar week containing the
 *  cursor, with weekday labels and the real `today` flagged (nowISO, independent of the cursor).
 *  Both the List agenda and the Board render over this same week data. */
export function weekModel(items: ViewItem[], cursorISO: string, nowISO: string, tz = TZ): WeekModel {
  const anchor = localDateStr(new Date(cursorISO), tz);
  const monday = addDays(anchor, -(weekdayMon1(anchor) - 1));
  const today = localDateStr(new Date(nowISO), tz);
  const [wf, wt] = windowISO(monday, addDays(monday, 7));
  const byDate = new Map<string, ViewItem[]>();
  for (const it of expandRecurringEvents(items, wf, wt)) {
    if (!isOpen(it)) continue;
    const due = dueDate(it, tz);
    if (due) (byDate.get(due) ?? byDate.set(due, []).get(due)!).push(it);
  }
  const days: DayGroup[] = [];
  for (let n = 0; n < 7; n++) {
    const d = addDays(monday, n);
    days.push({ date: d, label: WD_SHORT[weekdayMon1(d) - 1], items: ordered(byDate.get(d) ?? []) });
  }
  return { today, days };
}

// ── Date-window navigation (REQ-VIEW-011) — pure cursor shifting ──────────────────────────────
export type WindowScope = "today" | "week" | "month" | "trolley";

function addMonths(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const idx = m - 1 + n;
  const ny = y + Math.floor(idx / 12);
  const mo = ((idx % 12) + 12) % 12; // 0..11
  const daysInTarget = new Date(Date.UTC(ny, mo + 1, 0)).getUTCDate();
  return `${ny}-${pad2(mo + 1)}-${pad2(Math.min(d, daysInTarget))}`;
}

/** Shift the date cursor (a local YYYY-MM-DD) one window in `dir` for the active scope. */
export function shiftWindow(scope: WindowScope, cursorDate: string, dir: 1 | -1): string {
  if (scope === "week") return addDays(cursorDate, 7 * dir);
  if (scope === "month") return addMonths(cursorDate, dir);
  if (scope === "today") return addDays(cursorDate, dir);
  return cursorDate; // trolley has no date window
}

/** The canonical model ISO for a local date (NZ is +12/13, so 00:00Z lands mid-day local). */
export function cursorISO(cursorDate: string): string {
  return `${cursorDate}T00:00:00Z`;
}

// ── Month view (REQ-VIEW-010) — a calendar grid of weeks, tz/DST-aware ────────────────────────
export interface MonthCell {
  date: string; // YYYY-MM-DD
  day: number; // day-of-month
  inMonth: boolean; // false for the leading/trailing days of adjacent months
  items: ViewItem[];
}
export interface MonthModel {
  label: string; // "March 2026"
  year: number;
  month: number; // 1..12
  weeks: MonthCell[][]; // rows of 7 (Mon…Sun)
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const pad2 = (n: number) => String(n).padStart(2, "0");

export function monthModel(items: ViewItem[], cursorISO: string, tz = TZ): MonthModel {
  const cursor = localDateStr(new Date(cursorISO), tz);
  const [y, m] = cursor.split("-").map(Number); // month 1..12
  const first = `${y}-${pad2(m)}-01`;
  const start = addDays(first, -(weekdayMon1(first) - 1)); // Monday on/before the 1st
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const last = `${y}-${pad2(m)}-${pad2(daysInMonth)}`;
  const end = addDays(last, 7 - weekdayMon1(last)); // Sunday on/after the last

  const byDate = new Map<string, ViewItem[]>();
  for (const it of expandRecurringEvents(items, `${addDays(start, -2)}T00:00:00Z`, `${addDays(end, 2)}T00:00:00Z`)) {
    if (!isOpen(it)) continue;
    const due = dueDate(it, tz);
    if (due) (byDate.get(due) ?? byDate.set(due, []).get(due)!).push(it);
  }

  const monthPrefix = `${y}-${pad2(m)}`;
  const weeks: MonthCell[][] = [];
  let cur = start;
  while (cur <= end) {
    const week: MonthCell[] = [];
    for (let i = 0; i < 7; i++) {
      week.push({
        date: cur,
        day: Number(cur.split("-")[2]),
        inMonth: cur.slice(0, 7) === monthPrefix,
        items: ordered(byDate.get(cur) ?? []),
      });
      cur = addDays(cur, 1);
    }
    weeks.push(week);
  }
  return { label: `${MONTHS[m - 1]} ${y}`, year: y, month: m, weeks };
}

// ── Calendar navigation (REQ-VIEW-016) — one nav-driven surface, no mode segment ──────────────
// The Calendar holds Day / Week / Month, moved between by navigation: the header date opens Month
// (the "up" affordance), a Month day-cell opens Day, the Month Week column opens Week (VIEW-017),
// and prev/next step the active mode's window via shiftWindow. These transitions are pure — they
// just compute the next {mode, cursor}; main.ts applies them and repaints.
export type CalMode = "day" | "week" | "month";
export interface CalCursor {
  mode: CalMode;
  cursor: string; // local YYYY-MM-DD the window is anchored on
}
/** Header date → Month (keeps the cursor, so it reads as "up" from Day/Week). */
export function openMonth(cursor: string): CalCursor {
  return { mode: "month", cursor };
}
/** Month Week column → that Week. */
export function openWeek(cursor: string): CalCursor {
  return { mode: "week", cursor };
}
/** Month day-cell → that Day. */
export function openDay(cursor: string): CalCursor {
  return { mode: "day", cursor };
}

const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

/** The Calendar header label for the active mode + cursor (the date-as-context control). */
export function calTitle(mode: CalMode, cursorISO: string, tz = TZ): string {
  const date = localDateStr(new Date(cursorISO), tz);
  const [y, m, d] = date.split("-").map(Number);
  if (mode === "month") return `${MONTHS[m - 1]} ${y}`;
  if (mode === "day") return `${WD_SHORT[weekdayMon1(date) - 1]} ${d} ${MONTHS_SHORT[m - 1]}`;
  const monday = addDays(date, -(weekdayMon1(date) - 1));
  const sunday = addDays(monday, 6);
  const [, mm, md] = monday.split("-").map(Number);
  const [, sm, sd] = sunday.split("-").map(Number);
  return mm === sm
    ? `${md}–${sd} ${MONTHS_SHORT[mm - 1]}`
    : `${md} ${MONTHS_SHORT[mm - 1]} – ${sd} ${MONTHS_SHORT[sm - 1]}`;
}

// ── Week column context (REQ-VIEW-017) — ISO week-of-year + optional school term week ──────────
/** A school term as a local-date range (Monday start … Friday end). Supplied by 006; until then
 *  no term context exists, so termWeek returns null and only the ISO week-of-year renders. */
export interface TermSpan {
  start: string; // YYYY-MM-DD (term's first day)
  end: string; // YYYY-MM-DD (term's last day)
}
/** A term span carrying its term number (1..4) — for the "T3·4" Week-column label (REQ-CAL-010). */
export interface Term extends TermSpan {
  term: number;
}

/** The term + term-week for a date, or null outside every term (a holiday). Feeds the Week column
 *  label (REQ-VIEW-017/CAL-010). Term data comes from 006's subscribed feed; null until then. */
export function termContext(dateStr: string, terms?: Term[] | null): { term: number; week: number } | null {
  if (!terms) return null;
  for (const t of terms) {
    if (dateStr >= t.start && dateStr <= t.end) {
      const week = termWeek(dateStr, [t]);
      if (week !== null) return { term: t.term, week };
    }
  }
  return null;
}

/** ISO 8601 week-of-year for a local date (Mon-based; week 1 holds the year's first Thursday). */
export function isoWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  const dow = (t.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  t.setUTCDate(t.getUTCDate() + 3 - dow); // the Thursday of this week fixes the ISO year
  const w1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 4)); // Jan 4 is always in ISO week 1
  const w1dow = (w1.getUTCDay() + 6) % 7;
  return 1 + Math.round(((t.getTime() - w1.getTime()) / 86_400_000 - 3 + w1dow) / 7);
}

/** The term week for a date (1-based from the term's opening Monday), or null when the date is
 *  outside every supplied term — or when no term context exists yet (006 supplies it). */
export function termWeek(dateStr: string, terms?: TermSpan[] | null): number | null {
  if (!terms) return null;
  for (const term of terms) {
    if (dateStr >= term.start && dateStr <= term.end) {
      const mondayOf = (s: string) => addDays(s, -(weekdayMon1(s) - 1));
      const days = (Date.parse(`${mondayOf(dateStr)}T00:00:00Z`) - Date.parse(`${mondayOf(term.start)}T00:00:00Z`)) / 86_400_000;
      return Math.floor(days / 7) + 1;
    }
  }
  return null;
}

/** A single day's open items (Calendar Day mode) — the same projection the peek/week use. */
export function dayModel(items: ViewItem[], cursorISO: string, tz = TZ): DayGroup {
  const date = localDateStr(new Date(cursorISO), tz);
  const [wf, wt] = windowISO(date, date);
  const dayItems = expandRecurringEvents(items, wf, wt).filter((i) => isOpen(i) && dueDate(i, tz) === date);
  return { date, label: friendlyDate(date), items: ordered(dayItems) };
}

// ── Scope filter (REQ-VIEW-013) — All (family) vs Mine, over already-RLS-scoped rows ──────────
export type ScopeFilter = "all" | "mine";

/** Parent defaults to All; a child defaults to Mine. */
export function defaultFilter(role: string): ScopeFilter {
  return role === "child" ? "mine" : "all";
}
/** "mine" keeps only items I'm an owner of; "all" keeps everything the row set already contains.
 *  With multiple owners (REQ-ITEM-006), "mine" matches when I'm ANY of the owners. */
export function itemOwners(i: ViewItem): string[] {
  return i.owners && i.owners.length ? i.owners : i.owner_member_id ? [i.owner_member_id] : [];
}
export function filterByScope(items: ViewItem[], filter: ScopeFilter, meId: string): ViewItem[] {
  return filter === "mine" ? items.filter((i) => itemOwners(i).includes(meId)) : items;
}

// ── Trolley (REQ-GROCERY-001) — grocery items filtered into a shopping surface ────────────────
export function trolleyModel(items: ViewItem[]): ViewItem[] {
  return items
    .filter((i) => isOpen(i) && i.type === "grocery")
    .sort((a, b) => a.title.localeCompare(b.title));
}

// ── Private items (REQ-ITEM-005) — item.visibility; RLS hides others' private rows ────────────
export function isPrivate(it: ViewItem): boolean {
  return it.visibility === "private";
}
/** Toggle target for the private control (editable after creation). */
export function nextVisibility(current: string | null | undefined): "family" | "private" {
  return current === "private" ? "family" : "private";
}

// ── External-source seam (REQ-VIEW-007) — render-only, derived from `source`; no migration ────
export function isReadOnly(source: string | null | undefined): boolean {
  return typeof source === "string" && source.startsWith("external:");
}
/** A short badge label for an external source, or null for app-origin rows. */
export function sourceLabel(source: string | null | undefined): string | null {
  if (!isReadOnly(source)) return null;
  const raw = (source as string).slice("external:".length).replace(/[-_]+/g, " ").trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "External";
}

// ── Restatement (REQ-UX-001) — the draft as a plain sentence ──────────────────────────────────
const ARTICLE: Record<string, string> = {
  task: "a task",
  event: "an event",
  note: "a note",
  grocery: "a grocery",
};
/** A friendly relative day for a due date: today / tomorrow / weekday / date. */
export function relativeDayLabel(dueISO: string, nowISO?: string, tz = TZ): string {
  const due = localDateStr(new Date(dueISO), tz);
  if (!nowISO) return due;
  const today = localDateStr(new Date(nowISO), tz);
  if (due === today) return "today";
  if (due === addDays(today, 1)) return "tomorrow";
  for (let n = 2; n <= 6; n++) if (due === addDays(today, n)) return WD_LONG[weekdayMon1(due) - 1];
  return due;
}
export function restatementText(draft: Draft, nowISO?: string, tz = TZ): string {
  let s = `Got it — ${ARTICLE[draft.type] ?? "an item"}`;
  if (draft.owners.length) s += ` for ${draft.owners.join(" & ")}`;
  if (draft.due_at) s += `, due ${relativeDayLabel(draft.due_at, nowISO, tz)}`;
  return s + ".";
}

// ── Inferred-draft confirmation (REQ-UX-005) — a compact restatement, not a form ───────────────
const TYPE_WORD: Record<string, string> = { task: "Task", event: "Event", note: "Note", grocery: "Grocery" };
// Where an inferred type lands — "milk" → Trolley, not the calendar (capture-flow-spec §3).
const DEST_WORD: Record<string, string> = { grocery: "Trolley", event: "Calendar", task: "Tasks", note: "Notes" };

export type ConfirmationChipKind = "type" | "date" | "owner" | "dest";
export interface ConfirmationChip {
  text: string;
  kind: ConfirmationChipKind;
}
export interface ConfirmationModel {
  title: string;
  chips: ConfirmationChip[];
}

/** The compact confirmation for a ready draft: "Added: {title} · Task · Thu · Amelia · →Trolley".
 *  A pure model the render turns into an auto-dismissing role=status line (form only on Edit). */
export function confirmationModel(draft: Draft, nowISO?: string, tz = TZ): ConfirmationModel {
  const chips: ConfirmationChip[] = [{ text: TYPE_WORD[draft.type] ?? "Item", kind: "type" }];
  if (draft.due_at) chips.push({ text: relativeDayLabel(draft.due_at, nowISO, tz), kind: "date" });
  for (const o of draft.owners) chips.push({ text: o, kind: "owner" });
  chips.push({ text: DEST_WORD[draft.type] ?? "Items", kind: "dest" });
  return { title: draft.title, chips };
}

// ── View preference (REQ-VIEW-003) — persisted, Today default ─────────────────────────────────
export interface ViewStore {
  get(): string | null;
  set(v: string): void;
}
export function readViewPreference(store: ViewStore): ViewName {
  return store.get() === "week" ? "week" : "today";
}
export function writeViewPreference(store: ViewStore, v: ViewName): void {
  store.set(v);
}

// ── Presentation helpers (used by the See render) ─────────────────────────────────────────────
/** Friendly app-bar date, e.g. "Thu 11". */
export function friendlyDate(dateStr: string): string {
  const day = Number(dateStr.split("-")[2]);
  return `${WD_SHORT[weekdayMon1(dateStr) - 1]} ${day}`;
}

/** Local wall-clock time for an event, e.g. "3:00 pm". */
export function timeOfDay(dueISO: string, tz = TZ): string {
  const s = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(dueISO));
  return s.replace(" AM", " am").replace(" PM", " pm");
}

/** Stable owner→swatch index (1..6); 0 = unassigned/neutral. Decorative only. */
export function ownerColorIndex(id: string | null | undefined): number {
  if (!id) return 0;
  let h = 0;
  for (const ch of id) h = (h + ch.charCodeAt(0)) % 6;
  return h + 1;
}
